import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login, attachAppSumoLicense } from '../api/auth';
import aerochatLogo from '../assets/aerochat_logo_full.svg';
import '../styles/pages/login.css';

/**
 * Support-shared AppSumo activation page (NOT linked anywhere in the app).
 *
 * Support sends an EXISTING user a link of the form:
 *   /appsumo/redeem?license_key=<KEY>
 * The user enters their AeroChat email + password and clicks Redeem, which in
 * one step logs them in and attaches the license (POST /appsumo/attach-license,
 * which also flips use_react_admin=1 on their company). The app then routes them
 * to platform-selection / onboarding if that isn't done yet, otherwise the
 * dashboard.
 */
const AppSumoRedeemPage = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('appsumo');
  const [searchParams] = useSearchParams();
  const licenseKey = (searchParams.get('license_key') || '').trim();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const maskedKey = licenseKey.length > 12
    ? `${licenseKey.slice(0, 8)}-***-${licenseKey.slice(-4)}`
    : '***';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!licenseKey) {
      setError(t('redeemMissingKey'));
      return;
    }
    if (!email.trim() || !password.trim()) {
      setError(t('validation.fillRequired'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 1) Authenticate (stores the Bearer token).
      const data = await login(email.trim(), password);
      // 2) Attach the license in the same step — the backend links the license,
      //    swaps/inserts the AppSumo subscription, and sets use_react_admin=1.
      await attachAppSumoLicense({ licenseKey });
      // 3) Enter the app; AdminApp routes to /platform-select if onboarding
      //    isn't done yet, otherwise to the dashboard.
      onLoginSuccess?.(data.user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      // Surface the backend's message (wrong password, already redeemed, paid
      // plan, tier not configured, etc.).
      setError(err?.message || t('attach.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__logo">
          <img src={aerochatLogo} alt="AeroChat" className="login-card__logo-img" />
        </div>

        <div className="login-card__header">
          <h1 className="login-card__title">{t('redeemTitle')}</h1>
          <p className="login-card__subtitle">{t('redeemSubtitle')}</p>
        </div>

        <div className="login-card__plan-banner">
          <span className="login-card__plan-badge">{t('licenseBadge')}</span>
          <span className="login-card__plan-trial-text">
            {t('licenseRow', { key: maskedKey })}
          </span>
        </div>

        <form className="login-card__form" onSubmit={handleSubmit}>
          {error && <div className="login-card__error">{error}</div>}

          <div className="login-card__field">
            <label className="login-card__label">{t('emailLabel')}</label>
            <input
              type="email"
              className="login-card__input"
              placeholder={t('emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <div className="login-card__field">
            <label className="login-card__label">{t('passwordLabel')}</label>
            <div className="login-card__input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                className="login-card__input login-card__input--password"
                placeholder={t('passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="login-card__eye-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-card__submit login-card__submit--green"
            disabled={loading}
          >
            {loading ? t('redeeming') : t('redeem')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AppSumoRedeemPage;
